import React, { useState, useEffect, useRef } from "react";
import { Progress } from "antd";
import { LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined, StopOutlined, LinkOutlined } from "@ant-design/icons";
import { Modal } from "../../../design-system";
import nodeAPI from "../../../service/ApiServiceNode";
import "./ExportProgressModal.scss";
import logger from '../../../utils/logger';



/**
 * Export Progress Modal Component (Design System)
 * Shows real-time progress for async export jobs
 * Follows background-job-progress-pattern.md implementation
 * Refactored to use design-system Modal components
 */
const ExportProgressModal = ({
    visible,
    jobId,
    onComplete,
    onCancel,
    title = "Export Progress",
    statusEndpoint = "/finance/reports/sales-orders/exports",
    cancelEndpoint = "/finance/reports/sales-orders/exports"
}) => {
    const [jobStatus, setJobStatus] = useState(null);
    const [error, setError] = useState(null);
    const pollingInterval = useRef(null);
    const onCompleteRef = useRef(onComplete);
    const completedRef = useRef(false);

    // Keep onComplete ref up to date without triggering useEffect restart
    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    // Reset state when modal opens/closes or jobId changes
    useEffect(() => {
        if (visible && jobId) {
            setJobStatus(null);
            setError(null);
            completedRef.current = false;
        }
    }, [visible, jobId]);

    // Poll for job status every 500ms
    useEffect(() => {
        if (!visible || !jobId) {
            // Clean up polling when modal is closed
            if (pollingInterval.current) {
                clearInterval(pollingInterval.current);
                pollingInterval.current = null;
            }
            return;
        }

        // Start polling
        const pollStatus = async () => {
            try {
                const response = await nodeAPI.get(`${statusEndpoint}/${jobId}`);
                const status = response.data?.data || response.data;

                setJobStatus(status);

                // Stop polling if access is denied (e.g. session changed)
                if (status?.forbidden) {
                    if (pollingInterval.current) {
                        clearInterval(pollingInterval.current);
                        pollingInterval.current = null;
                    }
                    setError('Access denied. Please start a new export.');
                    return;
                }

                // Stop polling if job is completed, failed, or cancelled
                if (status?.state === 'completed' || status?.state === 'failed' || status?.state === 'cancelled') {
                    if (pollingInterval.current) {
                        clearInterval(pollingInterval.current);
                        pollingInterval.current = null;
                    }

                    // Auto-close on success after 2 seconds (only once)
                    if (status?.state === 'completed' && !completedRef.current) {
                        completedRef.current = true;
                        setTimeout(() => {
                            onCompleteRef.current?.(status.results);
                        }, 2000);
                    }
                }
            } catch (error) {
                logger.error('Error polling job status:', error);
                setError('Failed to fetch export status');
                if (pollingInterval.current) {
                    clearInterval(pollingInterval.current);
                    pollingInterval.current = null;
                }
            }
        };

        // Poll immediately
        pollStatus();

        // Then poll every 500ms
        pollingInterval.current = setInterval(pollStatus, 500);

        // Cleanup on unmount
        return () => {
            if (pollingInterval.current) {
                clearInterval(pollingInterval.current);
                pollingInterval.current = null;
            }
        };
    }, [visible, jobId, statusEndpoint]);

    const handleCancel = async () => {
        if (!jobId) {
            onCancel();
            return;
        }

        try {
            await nodeAPI.post(`${cancelEndpoint}/${jobId}`);
            // Status will be updated by next poll
        } catch (error) {
            logger.error('Error cancelling export:', error);
        }

        // Close modal immediately
        if (onCancel) {
            onCancel();
        }
    };

    const getStatusIcon = () => {
        if (!jobStatus) return <LoadingOutlined className="export-progress-modal__icon export-progress-modal__icon--loading" />;

        switch (jobStatus.state) {
            case 'completed':
                return <CheckCircleOutlined className="export-progress-modal__icon export-progress-modal__icon--success" />;
            case 'failed':
            case 'cancelled':
                return <CloseCircleOutlined className="export-progress-modal__icon export-progress-modal__icon--error" />;
            case 'active':
            case 'waiting':
            default:
                return <LoadingOutlined className="export-progress-modal__icon export-progress-modal__icon--loading" />;
        }
    };

    const getStatusColor = () => {
        if (!jobStatus) return 'normal';

        switch (jobStatus.state) {
            case 'completed':
                return 'success';
            case 'failed':
            case 'cancelled':
                return 'exception';
            case 'active':
                return 'active';
            case 'waiting':
            default:
                return 'normal';
        }
    };

    const getFooterButtons = () => {
        if (!jobStatus) return [];

        const state = jobStatus.state;

        if (state === 'completed') {
            const buttons = [];

            // Google Drive link button (only for Google exports)
            if (jobStatus.results?.fileUrl) {
                buttons.push({
                    key: 'open-file',
                    label: (
                        <a
                            href={jobStatus.results.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="export-progress-modal__link-button"
                        >
                            <LinkOutlined /> OPEN FILE
                        </a>
                    ),
                    variant: 'primary',
                    hoverColor: '#fad133',
                    onClick: (e) => {
                        // Let the <a> tag handle the navigation
                        e.stopPropagation();
                    }
                });
            }

            // Close button
            buttons.push({
                key: 'close',
                label: 'Close',
                variant: 'secondary',
                onClick: onCancel
            });

            return buttons;
        }

        if (state === 'failed' || state === 'cancelled') {
            return [{
                key: 'close',
                label: 'Close',
                variant: 'secondary',
                onClick: onCancel
            }];
        }

        // Active or waiting - show cancel button
        return [{
            key: 'cancel',
            label: 'Cancel',
            variant: 'danger',
            icon: <StopOutlined />,
            onClick: handleCancel
        }];
    };

    const progress = jobStatus?.progress || {};
    const percentage = progress.percentage || 0;
    const message = progress.message || 'Initializing...';
    const current = progress.current || 0;
    const total = progress.total || 0;

    const state = jobStatus?.state;

    // Determine if modal can be closed (only when not actively processing)
    const handleClose = (state === 'active' || state === 'waiting') ? null : onCancel;

    return (
        <Modal
            visible={visible}
            onClose={handleClose || (() => {})}
            width="small"
            maskClosable={false}
            className="export-progress-modal"
        >
            <Modal.Header title={title} />

            <Modal.Body>
                <div className="export-progress-modal__content">
                    {/* Status Icon */}
                    <div className="export-progress-modal__icon-container">
                        {getStatusIcon()}
                    </div>

                    {/* Progress Bar */}
                    <Progress
                        percent={percentage}
                        status={getStatusColor()}
                        strokeColor={{
                            '0%': '#FED230',
                            '100%': '#FEC578',
                        }}
                        className="export-progress-modal__progress-bar"
                    />

                    {/* Progress Message */}
                    <div className="export-progress-modal__message">
                        <p>{message}</p>
                    </div>

                    {/* Record Count (only show if total > 0) */}
                    {total > 0 && (
                        <div className="export-progress-modal__records">
                            <p>{current} of {total} records processed</p>
                        </div>
                    )}

                    {/* Batch Count (only show if currentBatch > 0) */}
                    {progress.currentBatch > 0 && (
                        <div className="export-progress-modal__batch">
                            <p>Batch {progress.currentBatch}</p>
                        </div>
                    )}

                    {/* Error Message */}
                    {jobStatus?.error && (
                        <div className="export-progress-modal__error">
                            <p>Error: {jobStatus.error}</p>
                        </div>
                    )}

                    {/* Generic Error */}
                    {error && (
                        <div className="export-progress-modal__error">
                            <p>{error}</p>
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer buttons={getFooterButtons()} showDefaultClose={false} />
        </Modal>
    );
};

export default ExportProgressModal;
